import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Input } from '../../../components/ui';
import { Trophy, Calendar, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

interface NumeroGanadorConSorteo {
  id: number;
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
  registrado_por: string;
  created_at: string;
  verificado: boolean;
}

const TIPOS_CONFIG: Record<string, { label: string; color: string }> = {
  NORMAL:    { label: 'Normal',    color: 'bg-blue-600' },
  REVENTADO: { label: 'Reventado', color: 'bg-orange-600' },
  MEGA:      { label: 'Mega',      color: 'bg-red-600' },
  MONAZOS:   { label: 'Monazos',   color: 'bg-purple-600' },
  FECHAS:    { label: 'Fechas',    color: 'bg-green-600' },
  PARLEY:    { label: 'Parley',    color: 'bg-pink-600' },
};

function formatearNumero(g: NumeroGanadorConSorteo): string {
  const p2 = (s: string) => s.padStart(2, '0');
  switch (g.tipo_id) {
    case 'NORMAL':
      return p2(g.num1);
    case 'REVENTADO': {
      const base = p2(g.num1);
      return g.tipo_premio ? `${base} (${g.tipo_premio})` : base;
    }
    case 'MEGA': {
      const base = p2(g.num1);
      return g.num2 ? `${base} / ${p2(g.num2)}` : base;
    }
    case 'MONAZOS': {
      const base = g.num1.padStart(3, '0');
      return g.tipo_monazos ? `${base} (${g.tipo_monazos})` : base;
    }
    case 'FECHAS':
      return g.num2 ? `${p2(g.num1)}-${p2(g.num2)}` : p2(g.num1);
    case 'PARLEY': {
      const partes = [g.num1];
      if (g.num2) partes.push(g.num2);
      if (g.num3) partes.push(g.num3);
      return partes.map(p2).join(' + ');
    }
    default:
      return g.num1;
  }
}

export const VendedorGanadoresPage: React.FC = () => {
  const [fechaFiltro, setFechaFiltro] = useState(() => fechaLocalYMD());
  const [ganadores, setGanadores] = useState<NumeroGanadorConSorteo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargarGanadores = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<NumeroGanadorConSorteo[]>(
        `/ganadores?fecha=${fechaFiltro}`
      );
      setGanadores(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar ganadores');
    } finally {
      setLoading(false);
    }
  }, [fechaFiltro]);

  useEffect(() => {
    cargarGanadores();
  }, [cargarGanadores]);

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Números Ganadores</h1>
          <p className="text-gray-500">Consulta los números ganadores por fecha</p>
        </div>
        <button
          onClick={cargarGanadores}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Filtro de fecha */}
      <Card>
        <div className="max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
          <Input
            type="date"
            value={fechaFiltro}
            onChange={(e) => setFechaFiltro(e.target.value)}
            leftIcon={<Calendar size={18} />}
          />
        </div>
      </Card>

      {/* Cargando */}
      {loading && (
        <div className="text-center py-12 text-gray-500">Cargando ganadores...</div>
      )}

      {/* Sin resultados */}
      {!loading && ganadores.length === 0 && (
        <Card>
          <div className="text-center py-10 text-gray-400">
            <Trophy size={40} className="mx-auto mb-3 opacity-30" />
            <p>No hay números ganadores registrados para esta fecha.</p>
          </div>
        </Card>
      )}

      {/* Tabla de ganadores */}
      {!loading && ganadores.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-3 pr-4">Sorteo</th>
                  <th className="py-3 pr-4">Tipo</th>
                  <th className="py-3 pr-4">
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      Cierre
                    </div>
                  </th>
                  <th className="py-3 pr-4">Número Ganador</th>
                  <th className="py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {ganadores.map((g) => {
                  const tipoCfg = TIPOS_CONFIG[g.tipo_id] || { label: g.tipo_id, color: 'bg-gray-500' };
                  return (
                    <tr key={g.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-900">{g.sorteo_nombre}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${tipoCfg.color}`}>
                          {tipoCfg.label}
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-mono text-gray-600">
                        {g.hora_cierre ? g.hora_cierre.slice(0, 5) : '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="font-bold text-lg font-mono text-blue-700">
                          {formatearNumero(g)}
                        </span>
                        {g.paga_reventado && (
                          <span className="ml-2 text-xs text-orange-600 font-medium">+ Reventado</span>
                        )}
                      </td>
                      <td className="py-3">
                        {g.verificado ? (
                          <Badge variant="success">
                            <CheckCircle size={12} className="inline mr-1" />
                            Verificado
                          </Badge>
                        ) : (
                          <Badge variant="warning">Pendiente</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            {ganadores.length} sorteo{ganadores.length !== 1 ? 's' : ''} con ganador para {fechaFiltro}
          </p>
        </Card>
      )}
    </div>
  );
};

export default VendedorGanadoresPage;
